import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Loader2, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { TitleHolder } from "./TitleHoldersStep";
import type { PropertyDetails } from "./PropertyDetailsStep";

interface ReviewConfirmStepProps {
  titleHolders: TitleHolder[];
  propertyDetails: PropertyDetails;
  onBack: () => void;
  onNext: () => void;
  onEditStep: (step: number) => void;
}

const ReviewConfirmStep = ({
  titleHolders,
  propertyDetails,
  onBack,
  onNext,
  onEditStep,
}: ReviewConfirmStepProps) => {
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleProceed = async () => {
    if (!confirmed) {
      toast({ title: "Please confirm the information is correct", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-marketing-agreement", {
        body: {
          action: "submit_full",
          title_holders: titleHolders,
          property_address: propertyDetails.property_address.trim(),
          city: propertyDetails.city.trim(),
          state: propertyDetails.state.trim(),
          zip_code: propertyDetails.zip_code.trim(),
          property_type: propertyDetails.property_type || null,
          bedrooms: propertyDetails.bedrooms ? parseInt(propertyDetails.bedrooms) : null,
          bathrooms: propertyDetails.bathrooms ? parseFloat(propertyDetails.bathrooms) : null,
          square_feet: propertyDetails.square_feet ? parseInt(propertyDetails.square_feet) : null,
          year_built: propertyDetails.year_built ? parseInt(propertyDetails.year_built) : null,
          condition: propertyDetails.condition || null,
          asking_price: propertyDetails.asking_price ? parseFloat(propertyDetails.asking_price) : null,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(typeof data.error === "string" ? data.error : "Failed to submit");

      onNext();
    } catch (err: any) {
      console.error("Submit error:", err);
      toast({ title: "Failed to submit agreement", description: err.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (val: string) => {
    if (!val) return "—";
    return `$${parseFloat(val).toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Review & Confirm</h2>
        <p className="text-slate-400">
          Please review your information before submitting.
        </p>
      </div>

      {/* Title Holders */}
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Title Holders</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditStep(1)}
            className="text-[#ffd700] hover:text-[#e6c200] gap-1.5"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        </div>
        <div className="space-y-3">
          {titleHolders.map((holder, idx) => (
            <div key={idx} className="bg-muted/30 rounded-md p-3 space-y-1">
              <p className="text-sm font-medium text-foreground">{holder.full_name || "—"}</p>
              <p className="text-xs text-muted-foreground">{holder.email || "—"}</p>
              <p className="text-xs text-muted-foreground">{holder.phone_number || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Property Details */}
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Property Details</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditStep(2)}
            className="text-[#ffd700] hover:text-[#e6c200] gap-1.5"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DetailItem label="Address" value={propertyDetails.property_address} />
            <DetailItem
              label="City, State, ZIP"
              value={
                [propertyDetails.city, propertyDetails.state, propertyDetails.zip_code]
                  .filter(Boolean)
                  .join(", ") || "—"
              }
            />
            <DetailItem label="Property Type" value={propertyDetails.property_type} />
            <DetailItem label="Condition" value={propertyDetails.condition} />
            <DetailItem label="Bedrooms" value={propertyDetails.bedrooms} />
            <DetailItem label="Bathrooms" value={propertyDetails.bathrooms} />
            <DetailItem label="Square Feet" value={propertyDetails.square_feet ? `${parseInt(propertyDetails.square_feet).toLocaleString()} sq ft` : ""} />
            <DetailItem label="Year Built" value={propertyDetails.year_built} />
          </div>
          <div className="pt-2 border-t border-border">
            <DetailItem label="Asking Price" value={formatPrice(propertyDetails.asking_price)} highlight />
          </div>
        </div>
      </div>

      {/* Confirmation */}
      <div className="bg-card border border-border rounded-lg p-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(checked === true)}
            className="mt-0.5 border-muted-foreground data-[state=checked]:bg-[#ffd700] data-[state=checked]:border-[#ffd700]"
          />
          <span className="text-sm text-foreground leading-relaxed">
            I confirm that the above information is correct and I am ready to submit.
          </span>
        </label>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} className="border-border">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={handleProceed}
          disabled={isSubmitting || !confirmed}
          className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] px-8 group"
          size="lg"
        >
          {isSubmitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Submit
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-sm ${highlight ? "font-semibold text-[#ffd700]" : "text-foreground"}`}>
      {value || "—"}
    </p>
  </div>
);

export default ReviewConfirmStep;